import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';

import { useNavigation } from '@react-navigation/native';

import { Text } from '@/components/TextComponent';
import { ds } from '@/components/lib/design.system';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { productNames } from '@/hooks/use-active-product-name';
import { useSwitchActiveWallet } from '@/hooks/use-active-wallet';
import { Product, getProductType, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useSocialWalletTranslation } from '@/hooks/use-social-wallet-translation';
import { getProductRoot } from '@/utils/product';
import { runAfterInteractions } from '@/utils/runAfterInteractions';

/**
 * Shared hook that navigates the user to the correct deposit entry point for a given product
 * once KYC has succeeded. Extracted from `VerificationSuccess` so the post-KYC
 * "default product" selection step can reuse the exact same continuation logic.
 *
 * - Sweeps/Casino -> `CoinPackageScreen`
 * - Everything else -> `DepositScreen`
 *
 * It also sets the active product, switches the active wallet and, for gameplay-offline
 * states, shows the "before you deposit" warning sheet before continuing.
 */
export const useNavigateToDeposit = () => {
    const navigation = useNavigation();
    const featureFlags = useJurisdictionStore(state => state.jurisdictionSettings?.globalSettings?.featureFlags);
    const setProduct = useJurisdictionStore(state => state.actions.setProduct);
    const switchActiveWallet = useSwitchActiveWallet();
    const { showInfoSheet } = useAlerts();
    const { t } = useTranslation(['kyc', 'common', 'transactions']);
    const tSocialWallet = useSocialWalletTranslation(['kyc', 'common', 'transactions']);

    const resetToDeposit = useCallback(
        (product: Product) => {
            const rootScreen = getProductRoot(product);

            if (product === Product.Sweeps || product === Product.Casino) {
                navigation.reset({
                    index: 0,
                    routes: [
                        { name: rootScreen },
                        {
                            name: 'DepositStack',
                            params: {
                                screen: 'CoinPackageScreen',
                                params: { isFromKyc: true },
                            },
                        },
                    ],
                });
                return;
            }

            navigation.reset({
                index: 0,
                routes: [
                    { name: rootScreen },
                    {
                        name: 'DepositStack',
                        params: {
                            screen: 'DepositScreen',
                            params: {
                                selectedAmount: '',
                                shouldRedirectToLobby: true,
                                isRedirectedFromVerificationSuccess: true,
                                forceDepositWithDebitCard: featureFlags?.kyc_CTA_debit_only?.enabled,
                            },
                        },
                    },
                ],
            });
        },
        [navigation, featureFlags?.kyc_CTA_debit_only]
    );

    const warnBeforeDeposit = useCallback(
        (product: Product, beforeYouDepositWarning: string) => {
            showInfoSheet({
                title: tSocialWallet('kyc:before_you_deposit'),
                description: (
                    <ScrollView style={styles.sheet}>
                        <Text color={ds.colorToken.text.secondary} variant="bodyMedium">
                            {beforeYouDepositWarning ?? ''}
                        </Text>
                    </ScrollView>
                ),
                buttonLabel: tSocialWallet('transactions:deposit_to_product', {
                    product: productNames[product],
                }),
                secondaryLabel: t('common:cancel'),
                handlePress: () => {
                    runAfterInteractions(() => {
                        resetToDeposit(product);
                    });
                },
            });
        },
        [resetToDeposit, showInfoSheet, t, tSocialWallet]
    );

    return useCallback(
        (product: Product) => {
            setProduct(product);
            switchActiveWallet(product);

            const { jurisdiction: usState, products, jurisdictionSettings } = useJurisdictionStore.getState();

            const config = products?.find(p => getProductType(p.uid) === product);
            const gameplayOfflineStates = config?.gameplayOfflineStates ?? [];
            const isGameplayOffline = gameplayOfflineStates.includes(usState ?? '');

            const beforeYouDepositData = jurisdictionSettings?.globalSettings?.keyValuePairs?.BeforeYouDepositInfo;
            const beforeYouDepositWarning = beforeYouDepositData?.[product] ?? '';

            runAfterInteractions(() => {
                if (isGameplayOffline && beforeYouDepositWarning) {
                    warnBeforeDeposit(product, beforeYouDepositWarning);
                } else {
                    resetToDeposit(product);
                }
            });
        },
        [resetToDeposit, setProduct, switchActiveWallet, warnBeforeDeposit]
    );
};

const styles = StyleSheet.create({
    sheet: { maxHeight: 400 },
});
